import { useState, useCallback, useMemo, useEffect } from "react";
import { DoorSize } from "../../../entities-generated/common/door-size";
import { IDoorSizeOption } from "../../../entities-generated/common/door-size-option";
import { IProductTexture } from "../../../entities-generated/common/product-texture";
import { StormDoorHandleType } from "../../../entities-generated/common/storm-door-handle-type";
import { IStormDoorHandleTypeOption } from "../../../entities-generated/common/storm-door-handle-type-option";
import { IStormDoorEditingModel, StormDoorEditingModel } from "../../../entities-generated/models/storm-door/storm-door-editing-model";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, MutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrUndefined, isNotNullOrWhitespace, isNullOrWhitespace } from "../../../framework/utils";
import { firstOrDefault, none, where } from "../../../framework/utils/collection";
import { StormDoorEvents } from "../events";
import { IDoorEditorFormModel, IDoorEditorValueModel, IDoorEditorModelChange } from "./door-editor-iface";
import { IUpdateStormDoorRequest } from "../../../entities-generated/models/storm-door/update-storm-door-request";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<IStormDoorEditingModel, string> {
    public initialize(data: IStormDoorEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:isActive/mutable/boolean/boolean%%
export const isActive = new (class isActiveService extends MutableFormValueService<IStormDoorEditingModel, boolean, boolean> {
    public initialize(data: IStormDoorEditingModel): void {
        this.value = data.isActive;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.isActive = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'isActive' // id
    ,'Door is actively displayed' // description
);
// %%END:isActive/mutable/boolean/boolean%%

// %%BEGIN:isNotAvailable/mutable/boolean/boolean%%
export const isNotAvailable = new (class isNotAvailableService extends MutableFormValueService<IStormDoorEditingModel, boolean, boolean> {
    public initialize(data: IStormDoorEditingModel): void {
        this.value = data.isNotAvailable;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.isNotAvailable = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'isNotAvailable' // id
    ,'Door is not available' // description
);
// %%END:isNotAvailable/mutable/boolean/boolean%%

// %%BEGIN:includesHandle/mutable/boolean/boolean%%
export const includesHandle = new (class includesHandleService extends MutableFormValueService<IStormDoorEditingModel, boolean, boolean> {
    public initialize(data: IStormDoorEditingModel): void {
        this.value = data.includesHandle;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.includesHandle = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'includesHandle' // id
    ,'Handle included with door' // description
);
// %%END:includesHandle/mutable/boolean/boolean%%

// %%BEGIN:cutdownPossible/mutable/boolean/boolean%%
export const cutdownPossible = new (class cutdownPossibleService extends MutableFormValueService<IStormDoorEditingModel, boolean, boolean> {
    public initialize(data: IStormDoorEditingModel): void {
        this.value = data.cutdownPossible;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.cutdownPossible = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'cutdownPossible' // id
    ,'Door can be cut down' // description
);
// %%END:cutdownPossible/mutable/boolean/boolean%%

// %%BEGIN:description/validated/string/string%%
export const description = new (class descriptionService extends ValidatedFormValueService<IStormDoorEditingModel, string, string> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.description));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.description = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter door description');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'description' // id
    ,'Description' // description
);
// %%END:description/validated/string/string%%

// %%BEGIN:brand/validated/string/string%%
export const brand = new (class brandService extends ValidatedFormValueService<IStormDoorEditingModel, string, string> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.brand));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.brand = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter storm door brand');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'brand' // id
    ,'Brand' // description
);
// %%END:brand/validated/string/string%%

// %%BEGIN:doorSize/validated/IDoorSizeOption | null/DoorSize%%
export const doorSize = new (class doorSizeService extends ValidatedFormValueService<IStormDoorEditingModel, IDoorSizeOption | null, DoorSize> {
    public initialize(data: IStormDoorEditingModel): void {
        this.options = data.doorSizes;
        const ds = firstOrDefault(this.options, d => d.id === data.doorSize);
        this.setInputValue(new BwjInput<IDoorSizeOption | null>(ds));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.doorSizes = this.options;
        data.doorSize = this.value.value?.id ?? DoorSize.Indeterminate;
    }

    public getDefaultValue(): BwjInput<IDoorSizeOption | null> {
        return new BwjInput<IDoorSizeOption | null>(null);
    }

    public getInputValue(): BwjInput<IDoorSizeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IDoorSizeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): DoorSize {
        const value = this.value.value;
        return value?.id ?? DoorSize.Indeterminate;
    }

    public validate(value: IDoorSizeOption | null): Promise<BwjInput<IDoorSizeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a door size');
        return Promise.resolve(result);
    }

    public onUpdate(value: IDoorSizeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IDoorSizeOption[] = [];
})(
    'doorSize' // id
    ,'Door size' // description
);
// %%END:doorSize/validated/IDoorSizeOption | null/DoorSize%%

// %%BEGIN:validHandlesets/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%
export const validHandlesets = new (class validHandlesetsService extends ValidatedFormValueService<IStormDoorEditingModel, IStormDoorHandleTypeOption | null, StormDoorHandleType> {
    public initialize(data: IStormDoorEditingModel): void {
        this.options = data.doorHandleTypes;
        const vhs = firstOrDefault(this.options, h => h.id === data.validHandlesets);
        this.setInputValue(new BwjInput<IStormDoorHandleTypeOption | null>(vhs));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.doorHandleTypes = this.options;
        data.validHandlesets = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return new BwjInput<IStormDoorHandleTypeOption | null>(null);
    }

    public getInputValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IStormDoorHandleTypeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): StormDoorHandleType {
        const value = this.value.value;
        return value?.id ?? StormDoorHandleType.Indeterminate;
    }

    public validate(value: IStormDoorHandleTypeOption | null): Promise<BwjInput<IStormDoorHandleTypeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a handle type');
        return Promise.resolve(result);
    }

    public onUpdate(value: IStormDoorHandleTypeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IStormDoorHandleTypeOption[] = [];
})(
    'validHandlesets' // id
    ,'Required handleset type' // description
);
// %%END:validHandlesets/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%

// %%BEGIN:imageUrls/validated/string/string[]%%
export const imageUrls = new (class imageUrlsService extends ValidatedFormValueService<IStormDoorEditingModel, string, string[]> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.imageUrls.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.imageUrls = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        const op = isNullOrWhitespace(value) ? [] : value.split('\n');
        return where(op, i => isNotNullOrWhitespace(i));
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'imageUrls' // id
    ,'Primary images' // description
);
// %%END:imageUrls/validated/string/string[]%%

// %%BEGIN:sellingPoints/validated/string/string[]%%
export const sellingPoints = new (class sellingPointsService extends ValidatedFormValueService<IStormDoorEditingModel, string, string[]> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.sellingPoints.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.sellingPoints = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        const op = isNullOrWhitespace(value) ? [] : value.split('\n');
        return where(op, i => isNotNullOrWhitespace(i));
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'sellingPoints' // id
    ,'Selling points' // description
);
// %%END:sellingPoints/validated/string/string[]%%

// %%BEGIN:price/validated/string/number%%
export const price = new (class priceService extends ValidatedFormValueService<IStormDoorEditingModel, string, number> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>((data.price / 100).toFixed(2)));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.price = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const v = this.value.value;
        let price = isNotNullOrWhitespace(v) ? parseFloat(v) : 0;
        price = isNaN(price) ? 0 : Math.floor(price * 100);
        return price;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter door price');
        if(result.validity === BwjValidityState.Valid) {
            const price = parseFloat(value);
            if(isNaN(price)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid price';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'price' // id
    ,'Price (including tax + pay proc fee)' // description
);
// %%END:price/validated/string/number%%

// %%BEGIN:additionalInstallLabor/validated/string/number | undefined%%
export const additionalInstallLabor = new (class additionalInstallLaborService extends ValidatedFormValueService<IStormDoorEditingModel, string, number | undefined> {
    public initialize(data: IStormDoorEditingModel): void {
        const val = isNotNullOrUndefined(data.additionalInstallLabor) ? data.additionalInstallLabor!.toString() : '';
        this.setInputValue(new BwjInput<string>(val));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.additionalInstallLabor = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        const str = this.value.value;
        const value = parseInt(str);
        return isNaN(value) === false ? value : undefined;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        if(isNotNullOrWhitespace(value)) {
            const num = parseInt(value);
            if(isNaN(num)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid integer';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'additionalInstallLabor' // id
    ,'Additional labor time' // description
);
// %%END:additionalInstallLabor/validated/string/number | undefined%%

// %%BEGIN:laborFeeCoefficient/validated/string/number | undefined%%
export const laborFeeCoefficient = new (class laborFeeCoefficientService extends ValidatedFormValueService<IStormDoorEditingModel, string, number | undefined> {
    public initialize(data: IStormDoorEditingModel): void {
        const val = isNotNullOrUndefined(data.laborFeeCoefficient) ? data.laborFeeCoefficient!.toString() : '';
        this.setInputValue(new BwjInput<string>(val));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.laborFeeCoefficient = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number | undefined {
        const str = this.value.value;
        const value = parseFloat(str);
        return isNaN(value) === false ? Math.abs(value) : undefined;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        if(isNotNullOrWhitespace(value)) {
            const num = parseFloat(value);
            if(isNaN(num)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid value';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'laborFeeCoefficient' // id
    ,'Labor fee coefficient' // description
);
// %%END:laborFeeCoefficient/validated/string/number | undefined%%

// %%BEGIN:inStoreQuantity/validated/string/number%%
export const inStoreQuantity = new (class inStoreQuantityService extends ValidatedFormValueService<IStormDoorEditingModel, string, number> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.inStoreQuantity.toString()));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.inStoreQuantity = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        let qty = parseInt(value);
        qty = isNaN(qty) === false ? qty : 0;
        return qty;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter the quantity in stock');
        if(result.validity === BwjValidityState.Valid) {
            const qty = parseInt(value);
            if(isNaN(qty)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid quantity';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'inStoreQuantity' // id
    ,'Quantity in stock' // description
);
// %%END:inStoreQuantity/validated/string/number%%

// %%BEGIN:pickupLeadTime/validated/string/number%%
export const pickupLeadTime = new (class pickupLeadTimeService extends ValidatedFormValueService<IStormDoorEditingModel, string, number> {
    public initialize(data: IStormDoorEditingModel): void {
        const d = data as any;
        data.pickupLeadTimeDays = d.pickupLeadTime_Days as number;
        this.setInputValue(new BwjInput<string>(data.pickupLeadTimeDays.toString()));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        const d = data as any;
        d.pickupLeadTime_Days =
        data.pickupLeadTimeDays =
        this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): number {
        const value = this.value.value;
        let ltd = parseInt(value);
        ltd = isNaN(ltd) === false ? ltd : 0;
        return ltd;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter pickup lead time');
        if(result.validity === BwjValidityState.Valid) {
            const price = parseInt(value);
            if(isNaN(price)) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Enter a valid lead time';
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'pickupLeadTime' // id
    ,'Pickup lead time (days)' // description
);
// %%END:pickupLeadTime/validated/string/number%%

// %%BEGIN:productReviewSummary/validated/string/string%%
export const productReviewSummary = new (class productReviewSummaryService extends ValidatedFormValueService<IStormDoorEditingModel, string, string> {
    public initialize(data: IStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.productReviewSummary));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.productReviewSummary = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'productReviewSummary' // id
    ,'Product review summary' // description
);
// %%END:productReviewSummary/validated/string/string%%

// %%BEGIN:textureVariants/mutable/IProductTexture[]/IProductTexture[]%%
export const textureVariants = new (class textureVariantsService extends MutableFormValueService<IStormDoorEditingModel, IProductTexture[], IProductTexture[]> {
    public initialize(data: IStormDoorEditingModel): void {
        this.value = data.textureVariants;
    }

    public applyToDataModel(data: IStormDoorEditingModel): void {
        data.textureVariants = this.value;
    }

    public getDefaultValue(): IProductTexture[] {
        return [];
    }

    public getInputValue(): IProductTexture[] {
        return this.value;
    }

    public setInputValue(value: IProductTexture[]): void {
        this.value = value;
    }

    public getOutputValue(): IProductTexture[] {
        return this.value;
    }

    public onUpdate(value: IProductTexture[]): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'textureVariants' // id
    ,'Available Product Variants' // description
);
// %%END:textureVariants/mutable/IProductTexture[]/IProductTexture[]%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IDoorEditorFormModel => {
    return {
       isActive: isActive.getInputValue(),
       isNotAvailable: isNotAvailable.getInputValue(),
       includesHandle: includesHandle.getInputValue(),
       cutdownPossible: cutdownPossible.getInputValue(),
       description: description.getInputValue(),
       brand: brand.getInputValue(),
       doorSize: doorSize.getInputValue(),
       validHandlesets: validHandlesets.getInputValue(),
       imageUrls: imageUrls.getInputValue(),
       sellingPoints: sellingPoints.getInputValue(),
       price: price.getInputValue(),
       additionalInstallLabor: additionalInstallLabor.getInputValue(),
       laborFeeCoefficient: laborFeeCoefficient.getInputValue(),
       inStoreQuantity: inStoreQuantity.getInputValue(),
       pickupLeadTime: pickupLeadTime.getInputValue(),
       productReviewSummary: productReviewSummary.getInputValue(),
       textureVariants: textureVariants.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IDoorEditorValueModel => {
    return {
       id: id.getOutputValue(),
       isActive: isActive.getOutputValue(),
       isNotAvailable: isNotAvailable.getOutputValue(),
       includesHandle: includesHandle.getOutputValue(),
       cutdownPossible: cutdownPossible.getOutputValue(),
       description: description.getOutputValue(),
       brand: brand.getOutputValue(),
       doorSize: doorSize.getOutputValue(),
       validHandlesets: validHandlesets.getOutputValue(),
       imageUrls: imageUrls.getOutputValue(),
       sellingPoints: sellingPoints.getOutputValue(),
       price: price.getOutputValue(),
       additionalInstallLabor: additionalInstallLabor.getOutputValue(),
       laborFeeCoefficient: laborFeeCoefficient.getOutputValue(),
       inStoreQuantity: inStoreQuantity.getOutputValue(),
       pickupLeadTime: pickupLeadTime.getOutputValue(),
       productReviewSummary: productReviewSummary.getOutputValue(),
       textureVariants: textureVariants.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        description.getInputValue(),
        brand.getInputValue(),
        doorSize.getInputValue(),
        validHandlesets.getInputValue(),
        imageUrls.getInputValue(),
        sellingPoints.getInputValue(),
        price.getInputValue(),
        additionalInstallLabor.getInputValue(),
        laborFeeCoefficient.getInputValue(),
        inStoreQuantity.getInputValue(),
        pickupLeadTime.getInputValue(),
        productReviewSummary.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IDoorEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(StormDoorEvents.EditFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IDoorEditorFormModel>> => {
    const promises = [
        description.formValidateAndUpdate(),
        brand.formValidateAndUpdate(),
        doorSize.formValidateAndUpdate(),
        validHandlesets.formValidateAndUpdate(),
        imageUrls.formValidateAndUpdate(),
        sellingPoints.formValidateAndUpdate(),
        price.formValidateAndUpdate(),
        additionalInstallLabor.formValidateAndUpdate(),
        laborFeeCoefficient.formValidateAndUpdate(),
        inStoreQuantity.formValidateAndUpdate(),
        pickupLeadTime.formValidateAndUpdate(),
        productReviewSummary.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IDoorEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IDoorEditorValueModel, dataModel: IStormDoorEditingModel } = {
    dataModel: new StormDoorEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IStormDoorEditingModel) => {
    const dataModel = data || new StormDoorEditingModel();

    id.initialize(dataModel);
    isActive.initialize(dataModel);
    isNotAvailable.initialize(dataModel);
    includesHandle.initialize(dataModel);
    cutdownPossible.initialize(dataModel);
    description.initialize(dataModel);
    brand.initialize(dataModel);
    doorSize.initialize(dataModel);
    validHandlesets.initialize(dataModel);
    imageUrls.initialize(dataModel);
    sellingPoints.initialize(dataModel);
    price.initialize(dataModel);
    additionalInstallLabor.initialize(dataModel);
    laborFeeCoefficient.initialize(dataModel);
    inStoreQuantity.initialize(dataModel);
    pickupLeadTime.initialize(dataModel);
    productReviewSummary.initialize(dataModel);
    textureVariants.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IDoorEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
isActive.setChangePublisher(broadcastChange);
isNotAvailable.setChangePublisher(broadcastChange);
includesHandle.setChangePublisher(broadcastChange);
cutdownPossible.setChangePublisher(broadcastChange);
description.setChangePublisher(broadcastChange);
brand.setChangePublisher(broadcastChange);
doorSize.setChangePublisher(broadcastChange);
validHandlesets.setChangePublisher(broadcastChange);
imageUrls.setChangePublisher(broadcastChange);
sellingPoints.setChangePublisher(broadcastChange);
price.setChangePublisher(broadcastChange);
additionalInstallLabor.setChangePublisher(broadcastChange);
laborFeeCoefficient.setChangePublisher(broadcastChange);
inStoreQuantity.setChangePublisher(broadcastChange);
pickupLeadTime.setChangePublisher(broadcastChange);
productReviewSummary.setChangePublisher(broadcastChange);
textureVariants.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new StormDoorEditingModel();

    id.applyToDataModel(dataModel);
    isActive.applyToDataModel(dataModel);
    isNotAvailable.applyToDataModel(dataModel);
    includesHandle.applyToDataModel(dataModel);
    cutdownPossible.applyToDataModel(dataModel);
    description.applyToDataModel(dataModel);
    brand.applyToDataModel(dataModel);
    doorSize.applyToDataModel(dataModel);
    validHandlesets.applyToDataModel(dataModel);
    imageUrls.applyToDataModel(dataModel);
    sellingPoints.applyToDataModel(dataModel);
    price.applyToDataModel(dataModel);
    additionalInstallLabor.applyToDataModel(dataModel);
    laborFeeCoefficient.applyToDataModel(dataModel);
    inStoreQuantity.applyToDataModel(dataModel);
    pickupLeadTime.applyToDataModel(dataModel);
    productReviewSummary.applyToDataModel(dataModel);
    textureVariants.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IDoorEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], StormDoorEvents.EditFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IDoorEditorFormModel => {
    const [getter, setter] = useState<IDoorEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IDoorEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IDoorEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('DoorEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('DoorEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toUpdateRequest = () => {
    const v = toValueModel();
    const familyId = v.description
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, ' ')
        .replace(/\s+/g, '-');

    const request: IUpdateStormDoorRequest = {
        id: v.id,
        isActive: v.isActive,
        description: v.description,
        brand: v.brand,
        familyId: familyId,
        doorSize: v.doorSize,
        validHandlesets: v.validHandlesets,
        includesHandle: v.includesHandle,
        cutdownPossible: v.cutdownPossible,
        imageUrls: v.imageUrls,
        sellingPoints: v.sellingPoints,
        textureVariants: v.textureVariants,
        price: v.price,
        inStoreQuantity: v.inStoreQuantity,
        isNotAvailable: v.isNotAvailable,
        pickupLeadTimeInDays: v.pickupLeadTime,
        productReviewSummary: v.productReviewSummary,
        additionalInstallLabor: v.additionalInstallLabor,
        laborFeeCoefficient: v.laborFeeCoefficient,
    };
    return request;
}

// ##END:DEVELOPERCODE##
